import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Amenity from "@/model/Amenity";
import useAmenityCatalog from "@/hooks/useAmenityCatalog";
import "@/components/AmenitiesDialog/AmenitiesDialog.scss";

interface AmenitiesDialogProps {
  amenities: Amenity[];
  onClose: () => void;
}

/** Every amenity of a listing, grouped by category with their descriptions (Airbnb's "Show all"). */
function AmenitiesDialog({ amenities, onClose }: AmenitiesDialogProps) {
  const { t } = useTranslation();
  const { categories } = useAmenityCatalog();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Lock the page behind the dialog, focus it, and close on Escape.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);

  const offered = new Set(amenities);
  const groups = categories
    .map((category) => ({
      ...category,
      amenities: category.amenities.filter((amenity) => offered.has(amenity.code)),
    }))
    .filter((category) => category.amenities.length > 0);

  return (
    <div className="amenities-dialog" onMouseDown={onClose}>
      <div
        className="amenities-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="amenities-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="amenities-dialog__header">
          <h2 id="amenities-dialog-title">{t("propertyDetails.amenitiesTitle")}</h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="amenities-dialog__close"
            aria-label={t("propertyDetails.closeAmenities")}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="amenities-dialog__body">
          {groups.map((category) => (
            <section key={category.code}>
              <h3>{category.label}</h3>
              <ul>
                {category.amenities.map((amenity) => (
                  <li key={amenity.code}>
                    <span>{amenity.label}</span>
                    {amenity.description && <small>{amenity.description}</small>}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AmenitiesDialog;
export type { AmenitiesDialogProps };
